import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import {
  UI_BUILD_HASH_PROPERTY,
  UI_BUILD_TAG_PROPERTY,
  UI_BUILD_TIME_PROPERTY,
} from '@erp/shared-utils';
import config from 'src/config';
import { isNetworkError } from './is-network-error';

export const ErrorCategory = {
  UserNetworkError: 'UserNetworkError',
  Uncategorized: 'Uncategorized',
} as const;

export type ErrorCategory = (typeof ErrorCategory)[keyof typeof ErrorCategory];

export function categorizeError(event: ErrorEvent): ErrorCategory {
  const message = event.exception?.values?.[0]?.value ?? event.message ?? '';

  if (isNetworkError(message)) {
    return ErrorCategory.UserNetworkError;
  }

  return ErrorCategory.Uncategorized;
}

const EXTENSION_URL_PATTERNS = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
  /^safari-web-extension:\/\//,
];

export const initSentry = () => {
  if (!config.sentryDsn) return;

  Sentry.init({
    dsn: config.sentryDsn,
    release: config.uiBuildTag || config.uiBuildHash || undefined,
    integrations: [Sentry.featureFlagsIntegration()],
    sendDefaultPii: true,
    // Catches the common case where the top frame is an extension URL.
    // beforeSend below handles frames deeper in the stack (Sentry v9 denyUrls
    // only checks the top frame).
    denyUrls: EXTENSION_URL_PATTERNS,
    beforeSend(event) {
      const hasExtensionFrame = event.exception?.values?.some((value) =>
        value.stacktrace?.frames?.some(
          (frame) =>
            typeof frame.filename === 'string' &&
            EXTENSION_URL_PATTERNS.some((pattern) =>
              pattern.test(frame.filename as string),
            ),
        ),
      );
      if (hasExtensionFrame) return null;

      const category = categorizeError(event);

      switch (category) {
        case ErrorCategory.UserNetworkError:
          event.level = 'info';
          event.tags = { ...event.tags, error_category: 'user_network_error' };
          event.fingerprint = ['user-network-error'];
          return event;
        default:
          return event;
      }
    },
  });

  Sentry.setTags({
    [UI_BUILD_HASH_PROPERTY]: config.uiBuildHash,
    [UI_BUILD_TIME_PROPERTY]: config.uiBuildTime,
    ...(config.uiBuildTag && {
      [UI_BUILD_TAG_PROPERTY]: config.uiBuildTag,
    }),
  });
};
